import {
  Component,
  Host,
  Prop,
  State,
  Element,
  h,
  Watch,
  getAssetPath
} from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";
import { Locale } from "../../common/locale";
import { config } from "../../common/config";

import { ActionListModel } from "@genexus/chameleon-controls-library";
import {
  ActionListItemActionable,
  ActionListItemGroup,
  ActionListItemModelExtended,
  ActionListItemSeparator
} from "@genexus/chameleon-controls-library/dist/types/components/action-list/types";

const FILTER_ICON = getIconPath({
  category: "system",
  name: "filters",
  colorType: "on-surface"
});

// plugin icon if no iconUrl was provided
const EXTENSION_ICON_IMG = getAssetPath(
  `./gx-ide-assets/plugin-explorer/images/plugin-icon.svg`
);

const CSS_BUNDLES: MercuryBundles = [
  "resets/box-sizing",
  "components/list-box",
  "components/button",
  "components/edit",
  "utils/form",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const SEARCH_ICON = getIconPath({
  category: "system",
  name: "search",
  colorType: "on-surface"
});

const STAR_ICON = getIconPath({
  category: "system",
  name: "star-fill",
  colorType: "warning"
});

const DOWNLOAD_ICON = getIconPath({
  category: "system",
  name: "download",
  colorType: "on-surface"
});

@Component({
  tag: "gx-ide-plugin-explorer",
  styleUrl: "plugin-explorer.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/plugin-explorer"]
})
export class GxIdePluginManager {
  /**
   * The component hard-coded strings translations.
   */
  // eslint-disable-next-line @stencil-community/own-props-must-be-private
  #componentLocale: any;
  #animateEmptyState: boolean = false;

  @Element() el: HTMLGxIdePluginExplorerElement;

  /**
   * If true it will hide the content, and display a loader
   */
  @State() loading: boolean = true;

  /**
   * The plugins model as ActionListModel, which is the model the ch-action-list expects
   */
  @State() pluginsActionListModel: ActionListModel = [];

  /**
   * List of plugin groups to display. Each group is rendered as a native
   * action-list group (with a separator between groups) and its header caption
   * includes the total number of plugins in the group.
   * Usually used to initialize the component.
   */
  @Prop({ mutable: true }) plugins: PluginGroupData[];
  @Watch("plugins")
  pluginsChangedHandler(newPlugins: PluginGroupData[]) {
    this.pluginsActionListModel =
      this.#mapPluginGroupsToActionListModel(newPlugins);
    this.loading = false;
  }

  /**
   * Callback that should be invoked when the user updates the filter.
   * A debounce should be applied before invoking this function.
   */
  @Prop() readonly filterCallback!: (
    text: string
  ) => Promise<PluginGroupData[]>;

  /**
   * Callback that should be invoked when the user clicks or presses Enter
   * on a selected plugin item.
   */
  @Prop() readonly showDetailsCallback!: (pluginId: string) => Promise<void>;

  /**
   * Callback that should be invoked when the user clicks on a plugin's action button.
   */
  @Prop() readonly executeActionCallback!: (
    pluginId: string,
    action: PluginAction
  ) => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.pluginsChangedHandler(this.plugins);
  }

  #actionListButtonClickHandler =
    (pluginId: string, action: PluginAction) => (event: MouseEvent) => {
      event.stopPropagation();
      this.executeActionCallback(pluginId, action);
    };

  // Formats a raw downloads count into a compact string (e.g. 4000 -> "4K").
  #downloadsFormatter = new Intl.NumberFormat(undefined, {
    notation: "compact",
    maximumFractionDigits: 1
  });

  #formatDownloads = (value: number): string => {
    const numericValue = typeof value === "number" ? value : Number(value);
    return Number.isFinite(numericValue)
      ? this.#downloadsFormatter.format(numericValue)
      : `${value}`;
  };

  #mapPluginToItem = (pluginData: PluginData): ActionListItemActionable => {
    const hasActionButton = !!pluginData.action;
    const hasDownloads =
      pluginData.downloads !== undefined && pluginData.downloads !== null;
    const hasRating =
      pluginData.rating !== undefined && pluginData.rating !== null;
    const hasMeta = hasDownloads || hasRating;

    const pluginItem = {
      id: pluginData.id,
      caption: pluginData.name,
      imgSrc: pluginData.iconUrl,
      type: "actionable",

      additionalInformation: {
        "stretch-start": {
          center: [
            {
              part: "custom-image",
              jsx: () => (
                <img
                  part="custom-image"
                  src={pluginData.iconUrl || EXTENSION_ICON_IMG}
                  alt={`${this.#componentLocale.logotype.replace(
                    "PLUGIN_NAME_PLACEHOLDER",
                    pluginData.name
                  )}`}
                  loading="lazy"
                />
              )
            }
          ]
        },
        "inline-caption": hasMeta && {
          end: [
            {
              part: "custom-meta",
              jsx: () => (
                <div part="custom-meta">
                  {hasDownloads && (
                    <span
                      part="custom-meta-item custom-downloads"
                      title={`${pluginData.downloads}`}
                    >
                      <ch-image
                        part="custom-meta-icon custom-downloads-icon"
                        src={DOWNLOAD_ICON}
                      ></ch-image>
                      {this.#formatDownloads(pluginData.downloads)}
                    </span>
                  )}
                  {hasRating && (
                    <span
                      part="custom-meta-item custom-rating"
                      title={`${pluginData.rating}`}
                    >
                      <ch-image
                        part="custom-meta-icon custom-rating-icon"
                        src={STAR_ICON}
                      ></ch-image>
                      {pluginData.rating}
                    </span>
                  )}
                </div>
              )
            }
          ]
        },
        "block-end": {
          start: [
            {
              part: "custom-description custom-button custom-container custom-brand custom-footer custom-meta custom-meta-item custom-meta-icon custom-progress custom-progress-label custom-progress-bar item__block-end start",
              jsx: () => (
                <div part="custom-container">
                  <p part="custom-description" title={pluginData.description}>
                    {pluginData.description}
                  </p>
                  <div part="custom-footer">
                    <small part="custom-brand">{pluginData.publisher}</small>
                    {pluginData.installProgress ? (
                      <div part="custom-progress">
                        <span part="custom-progress-label">
                          {pluginData.installProgress.percent}%
                        </span>
                        <gx-ide-progress-bar
                          part="custom-progress-bar"
                          class="plain"
                          minimal
                          active
                          stateType="in-progress"
                          progress={pluginData.installProgress.percent}
                        ></gx-ide-progress-bar>
                      </div>
                    ) : hasActionButton ? (
                      <button
                        class="button"
                        part="custom-button"
                        onClick={this.#actionListButtonClickHandler(
                          pluginData.id,
                          pluginData.action
                        )}
                      >
                        {pluginData.action}
                      </button>
                    ) : null}
                  </div>
                </div>
              )
            }
            // {
            //   slot: "abcd"
            // } as ActionListItemAdditionalSlot // slot not working
          ]
        }
      }
    } as ActionListItemActionable;

    return pluginItem;
  };

  // Builds the ch-action-list model from groups: one native action-list group
  // per PluginGroupData, plus a separator between groups. The group header only
  // renders a plain-text caption (no custom pill element is supported), so the
  // per-group total is shown as a number appended to the caption.
  #mapPluginGroupsToActionListModel = (
    groups: PluginGroupData[]
  ): ActionListModel => {
    const model: ActionListModel = [];
    const separatorItem: ActionListItemSeparator = { type: "separator" };

    groups?.forEach(group => {
      // Skip empty groups so we don't render a header with "(0)".
      if (!group.plugins?.length) {
        return;
      }
      if (model.length > 0) {
        model.push(separatorItem);
      }
      model.push({
        id: group.id,
        type: "group",
        caption: `${group.caption} (${group.plugins.length})`,
        expandable: group.expandable ?? true,
        expanded: group.expanded ?? true,
        items: group.plugins.map(plugin => this.#mapPluginToItem(plugin))
      } as ActionListItemGroup);
    });

    return model;
  };

  #pluginsFilterInputHandler = async (
    event: CustomEvent<string> | InputEvent
  ) => {
    this.loading = true;
    const filterValue = event.detail as string;
    this.plugins = await this.filterCallback(filterValue);
  };

  #evaluateContentToDisplay = () => {
    const pluginsToDisplay = this.pluginsActionListModel.length;
    return pluginsToDisplay ? (
      <ch-action-list-render
        model={this.pluginsActionListModel}
        class="list-box"
        selection="single"
        onSelectedItemsChange={this.#pluginSelectedHandler}
      >
        {/* <p slot="abcd">Slot for "abcd" id</p> */}
      </ch-action-list-render>
    ) : (
      <gx-ide-empty-state
        isAnimated={this.#animateEmptyState}
        class="recent-objects__empty-state"
        stateIconSrc={FILTER_ICON}
        stateTitle={this.#componentLocale.emptyStateTitle}
        stateDescription={this.#componentLocale.emptyStateDescription}
      ></gx-ide-empty-state>
    );
  };

  #renderLoader = () => {
    this.#animateEmptyState = true;
    return (
      <gx-ide-loader
        loaderTitle={this.#componentLocale.loaderTitle}
        description={this.#componentLocale.loaderDescription}
        show
      ></gx-ide-loader>
    );
  };

  #pluginSelectedHandler = (
    event: CustomEvent<ActionListItemModelExtended[]>
  ) => {
    const eventType = event.detail[0]?.item.type;
    if (eventType === "actionable") {
      const pluginId = event.detail[0].item.id;
      this.showDetailsCallback(pluginId);
    }
  };

  render() {
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>

        <div class="outer-container field-group">
          <ch-edit
            id="search-plugins-input"
            class="input"
            accessibleName={this.#componentLocale.accessibleName}
            placeholder={this.#componentLocale.searchInputPlaceholder}
            debounce={config.inputDebounce}
            onInput={this.#pluginsFilterInputHandler}
            type="search"
            startImgSrc={SEARCH_ICON}
          ></ch-edit>
          <div class="inner-container">
            {this.loading
              ? this.#renderLoader()
              : this.#evaluateContentToDisplay()}
          </div>
        </div>
      </Host>
    );
  }
}

export type PluginGroupData = {
  id: string;
  /**
   * Group header text. The total number of plugins in the group is appended
   * automatically, e.g. "Installed (12)".
   */
  caption: string;
  /**
   * Whether the group can be collapsed/expanded. Defaults to `true`.
   */
  expandable?: boolean;
  /**
   * Whether the group starts expanded. Defaults to `true`.
   */
  expanded?: boolean;
  plugins: PluginData[];
};

export type PluginData = {
  id: string;
  iconUrl: string;
  name: string;
  description: string;
  publisher: string;
  action?: PluginAction;
  downloads?: number;
  rating?: string | number;
  // When set, the card shows a determinate progress bar instead of the action button.
  installProgress?: PluginInstallProgress;
};

export type PluginInstallProgress = {
  phase: "download" | "install";
  percent: number;
};

export type PluginAction = "install" | "update";
